/**
 * Light and dark.
 *
 * The stylesheet defines the dark palette twice — once under
 * prefers-color-scheme and once under [data-theme='dark'] — so the page is
 * already the right colour before this module runs. There is no inline boot
 * script because the CSP allows none, and there is no flash because the CSS,
 * not the JavaScript, holds the default.
 */

import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const KEY = 'vouchkeep.theme';

function read(): Theme {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Storage can be denied; the system setting is a fine answer.
  }
  return 'system';
}

function apply(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

export function useTheme(): { theme: Theme; setTheme: (next: Theme) => void; isDark: boolean } {
  const [theme, setThemeState] = useState<Theme>(read);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    apply(theme);
  }, [theme]);

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setSystemDark(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  const setTheme = (next: Theme) => {
    setThemeState(next);
    try {
      if (next === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, next);
    } catch {
      // A theme that does not survive a reload still beats an exception.
    }
  };

  return { theme, setTheme, isDark: theme === 'dark' || (theme === 'system' && systemDark) };
}
